import { useEffect, useRef, useState } from "react";
import { useKeyboardOffset as useRawKeyboardOffset } from "@/hooks/useKeyboardOffset";

/**
 * State-machine wrapper around useKeyboardOffset that eliminates iOS typing
 * flicker caused by rapid visualViewport.resize events while the keyboard
 * is open.
 *
 * Three-phase state machine:
 *
 *   'closed'   raw=0, stable=0. Waiting for keyboard to open.
 *
 *   'settling' raw>0. Keyboard is animating up. Stable updates in
 *              OPEN_STEP_PX increments (not on every event) so the input
 *              bar tracks the keyboard in ~3 smooth steps rather than with
 *              dozens of overlapping 150ms CSS transitions. The one-shot
 *              lock timer is set ONCE at phase entry and never reset, so
 *              typing within the first 300ms doesn't prevent the lock from
 *              firing. When the timer fires, stable is set to rawRef.current
 *              (the keyboard's final resting height) and phase → 'open'.
 *
 *   'open'     stable FROZEN. All raw wobble (predictive-text bar changing
 *              height on each keystroke) is silently dropped — this stops
 *              typing flicker. Exits only when raw===0 (keyboard dismissed).
 *
 * History:
 *   v1 (June 23 2026): timer was reset on every raw change during settling →
 *      never reached 'open' if user typed quickly → flicker persisted.
 *   v2 (same day): timer not reset, but stable updated on every raw event →
 *      dozens of overlapping CSS transitions during keyboard open → visible
 *      judder and "jumps to mid-screen" on initial tap.
 *   v3 (same day): timer not reset + step-throttle stable updates during
 *      settling → ~3 smooth jumps during open animation, then locked.
 *
 * Does NOT modify useKeyboardOffset.ts (fragile per June 22 2026 notes).
 */

const SETTLE_MS = 300;
// Minimum raw increase from the last published stable before we emit another
// stable update during the keyboard-open animation. Keeps overlapping CSS
// transitions to ~3 per open event instead of 10+.
const OPEN_STEP_PX = 100;
// Hard ceiling on any stable value we will ever publish. No iPhone keyboard
// (keys + accessory bar) can legitimately exceed ~500px. In Median's WKWebView,
// iOS sometimes scrolls the content to keep the focused input above the keyboard,
// making vv.offsetTop go negative. The VV formula misreads that scroll as
// keyboard height and returns an inflated value (observed: ~700px on a device
// where the real keyboard is ~477px). Capping at 600 rejects those inflated
// values while accepting every real keyboard height on any supported device.
const OPEN_MAX_PX = 600;
// How long raw must stay at 0 before we treat it as a genuine keyboard close.
// Median emits transient visible:false events during predictive-bar adjustments;
// without this guard those cancel the settle timer and reset the phase prematurely.
const CLOSE_CONFIRM_MS = 250;

/**
 * Self-healing diagnostics write, mirroring `diagSrc()` in useKeyboardOffset.ts
 * and the rule stated there: "Diagnostics must never be able to throw inside
 * the keyboard path — a crash here would be worse than the bug being
 * diagnosed."
 *
 * Aug 20 2026. The six writes below used to poke the __kbDiag global directly,
 * which works only because importing useKeyboardOffset creates that global as
 * an import SIDE EFFECT. That made a debug channel load-bearing for not
 * crashing: if __kbDiag is ever absent, the TypeError lands inside a passive
 * effect, React unmounts the subtree, and an open BottomSheet remounts —
 * replaying `animate-fade-slide-in` as a visible slide. Found when a profiling
 * harness mocked useKeyboardOffset away and the sheet crashed on mount.
 *
 * Nothing in app code clears __kbDiag today, so this was latent rather than
 * live. It is guarded now so it cannot become live.
 */
const writeStableDiag = (value: number, phase: string, raw: number): void => {
  const w = window as unknown as Record<string, any>;
  w.__kbDiag = w.__kbDiag || {};
  w.__kbDiag.stable = { value, phase, raw };
};

export function useStableKeyboardOffset(): number {
  const raw = useRawKeyboardOffset();
  const rawRef = useRef(raw);
  const [stable, setStable] = useState(0);
  // Tracks the last value passed to setStable so the step-throttle in
  // 'settling' can compute distance without a stale closure.
  const stableRef = useRef(0);
  const phaseRef = useRef<"closed" | "settling" | "open">("closed");
  const timerRef = useRef<number | null>(null);
  // Pending confirm-close debounce. Started on raw===0; canceled on any non-zero raw.
  const closeTimerRef = useRef<number | null>(null);

  // Keep rawRef current so the one-shot timer closure captures the keyboard's
  // final resting height (not the value from when the timer was scheduled).
  rawRef.current = raw;

  useEffect(() => {
    const phase = phaseRef.current;

    if (phase === "closed") {
      if (raw > 0) {
        // Keyboard starting to open. Set stable to initial raw (usually the
        // focus sentinel value ≈1) so keyboardOpen flips true immediately.
        phaseRef.current = "settling";
        stableRef.current = raw;
        writeStableDiag(raw, phaseRef.current, rawRef.current);
        console.log("[KB-DIAG] stable-set", { newStable: raw, raw: rawRef.current, phase: phaseRef.current });
        setStable(raw);
        // ONE-SHOT timer — never reset during settling. Fires SETTLE_MS after
        // the keyboard first appears; by then the slide-up animation is done.
        timerRef.current = window.setTimeout(() => {
          phaseRef.current = "open";
          // Always update diag so the strip shows ph=open even when setStable isn't called.
          writeStableDiag(stableRef.current, phaseRef.current, rawRef.current);
          // Only advance stable — never let a stale low rawRef.current override
          // the correct value already published by the step mechanism.
          if (rawRef.current > stableRef.current && rawRef.current <= OPEN_MAX_PX) {
            stableRef.current = rawRef.current;
            writeStableDiag(rawRef.current, phaseRef.current, rawRef.current);
            console.log("[KB-DIAG] stable-set", { newStable: rawRef.current, raw: rawRef.current, phase: phaseRef.current });
            setStable(rawRef.current);
          }
          timerRef.current = null;
        }, SETTLE_MS);
      }
    } else if (phase === "settling") {
      if (raw === 0) {
        // Don't cancel immediately — could be a transient Median visible:false.
        // Only truly close after CLOSE_CONFIRM_MS of sustained raw===0.
        if (closeTimerRef.current === null) {
          closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null;
            if (timerRef.current !== null) {
              window.clearTimeout(timerRef.current);
              timerRef.current = null;
            }
            phaseRef.current = "closed";
            stableRef.current = 0;
            writeStableDiag(0, phaseRef.current, rawRef.current);
            console.log("[KB-DIAG] stable-set", { newStable: 0, raw: rawRef.current, phase: phaseRef.current });
            setStable(0);
          }, CLOSE_CONFIRM_MS);
        }
      } else {
        // Non-zero raw: keyboard is still up. Cancel any pending confirm-close.
        if (closeTimerRef.current !== null) {
          window.clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        if (raw - stableRef.current >= OPEN_STEP_PX && raw <= OPEN_MAX_PX) {
          // Keyboard moved up by a significant step — publish this milestone.
          // Signed subtraction (not Math.abs) so only upward motion triggers;
          // small wobbles or brief dips are skipped. OPEN_MAX_PX guards against
          // inflated vv.offsetTop values from WKWebView content-scroll-to-focus.
          stableRef.current = raw;
          writeStableDiag(raw, phaseRef.current, rawRef.current);
          console.log("[KB-DIAG] stable-set", { newStable: raw, raw: rawRef.current, phase: phaseRef.current });
          setStable(raw);
          // Timer continues running — NOT reset.
        }
      }
    } else {
      // "open": keyboard fully settled. Freeze stable; drop all raw wobble.
      if (raw === 0) {
        // Don't close immediately — could be a transient Median visible:false.
        if (closeTimerRef.current === null) {
          closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null;
            phaseRef.current = "closed";
            stableRef.current = 0;
            writeStableDiag(0, phaseRef.current, rawRef.current);
            console.log("[KB-DIAG] stable-set", { newStable: 0, raw: rawRef.current, phase: phaseRef.current });
            setStable(0);
          }, CLOSE_CONFIRM_MS);
        }
      } else {
        // Non-zero raw: cancel any pending confirm-close.
        if (closeTimerRef.current !== null) {
          window.clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
      }
    }
  }, [raw]);

  // Cleanup timers on unmount only.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  return stable;
}
